import React, { useEffect, useMemo, useState } from 'react';
import styles from './LinkedDiagnosticsPopup.module.css';
import LoadingIcon from '../../../Utils/LoadingIcon/LoadingIcon';
import Button from '../../../Utils/Button/Button';
import { GetImprovementById } from '../../../../API/Improve/Improve';
import {
  GetEvaluateQuestionByReference,
  GetEvaluateQuestionByTrimmedReference,
  GetQuestionnaireByAreaId,
} from '../../../../API/Evaluate/Evaluate';
import { IProductQuestionnaire } from '../../../../Types/Products/IProductQuestionnaire';
import { getAreasForImprovement } from '../../../../Types/Products/ICriteriaItemData';
import {
  EvaluateQuestion,
  EvaluateQuestionReference,
  LinkedDiagnosticReferences,
  ProductLink,
} from '../../../../Types/Improve/LinkedDiagnostics';
import { useNavigate } from 'react-router-dom';
import { ProductType, getProductTypePathSegment } from '../../../../Types/ProductType';
import { useAppSelector } from '../../../../Redux/Store/Store';
import { getDevelopEnabled } from '../../../../Redux/Store/Slices/client';

type LinkedDiagnosticsPopupProps = {
  objectiveId?: string;
};

type DiagnosticSource = 'evaluate' | 'develop' | 'enquire';

type LinkedQuestionItem = {
  questionRef: EvaluateQuestionReference;
  question: EvaluateQuestion | null;
  source: DiagnosticSource;
};

const getAreaId = (reference: EvaluateQuestionReference): string =>
  reference.AreaId ?? reference.areaId ?? '';

const getQuestionId = (reference: EvaluateQuestionReference): string =>
  reference.QuestionId ?? reference.questionId ?? '';

const getQuestionGroupId = (reference: EvaluateQuestionReference): string | undefined =>
  reference.QuestionGroupId ?? reference.questionGroupId ?? reference.QuestionGroup?.id ?? reference.questionGroup?.id;

const getQuestionTitle = (question: EvaluateQuestion): string =>
  question.title ?? question.Title ?? '';

const getFullQuestion = (question: EvaluateQuestion): string =>
  question.fullQuestion ?? question.FullQuestion ?? '';

const getAreasForImprovementText = (question: EvaluateQuestion): string =>
  question.areasForImprovement ?? question.AreasForImprovement ?? '';

const getQuestionTitleSafe = (question: EvaluateQuestion | null): string =>
  question ? getQuestionTitle(question) : '';

const getFullQuestionSafe = (question: EvaluateQuestion | null): string =>
  question ? getFullQuestion(question) : '';

const getAreasForImprovementSafe = (question: EvaluateQuestion | null): string =>
  question ? getAreasForImprovementText(question) : '';

const asReferenceList = (value: LinkedDiagnosticReferences | undefined): EvaluateQuestionReference[] => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
};

const getLinkAreaId = (link: ProductLink): string =>
  link.developId ?? link.areaId ?? '';

const sourceForProductType = (productType?: ProductType): DiagnosticSource => {
  if (productType === ProductType.Develop) return 'develop';
  if (productType === ProductType.Enquire) return 'enquire';
  return 'evaluate';
};

const productTypeForSource = (source: DiagnosticSource): ProductType => {
  if (source === 'develop') return ProductType.Develop;
  if (source === 'enquire') return ProductType.Enquire;
  return ProductType.Evaluate;
};

const LinkedDiagnosticsPopup: React.FC<LinkedDiagnosticsPopupProps> = ({ objectiveId }) => {
  const navigate = useNavigate();
  const developEnabled = useAppSelector(getDevelopEnabled);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionItems, setQuestionItems] = useState<LinkedQuestionItem[]>([]);
  const [areaTitlesById, setAreaTitlesById] = useState<Record<string, string>>({});
  const [questionnaireByAreaId, setQuestionnaireByAreaId] = useState<Record<string, IProductQuestionnaire>>({});

  useEffect(() => {
    const loadLinkedDiagnostics = async () => {
      if (!objectiveId) {
        setQuestionItems([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const improvement = await GetImprovementById(objectiveId);

        const evaluateRefs = asReferenceList(improvement.linkedEvaluateAreas);
        const developRefs = asReferenceList(improvement.linkedDevelopCriteria);
        const enquireRefs = asReferenceList(improvement.linkedEnquireAreas);

        const developAreaIdsFromCriteria = new Set(developRefs.map(getAreaId).filter(Boolean));
        const extraDevelopRefs = (improvement.developLinks ?? [])
          .map(getLinkAreaId)
          .filter(areaId => areaId && !developAreaIdsFromCriteria.has(areaId))
          .map((areaId): EvaluateQuestionReference => ({ AreaId: areaId }));

        const enquireAreaIdsFromCriteria = new Set(enquireRefs.map(getAreaId).filter(Boolean));
        const extraEnquireRefs = (improvement.enquireLinks ?? [])
          .map(getLinkAreaId)
          .filter(areaId => areaId && !enquireAreaIdsFromCriteria.has(areaId))
          .map((areaId): EvaluateQuestionReference => ({ AreaId: areaId }));

        const linkedItems: LinkedQuestionItem[] = [
          ...evaluateRefs.map(questionRef => ({ questionRef, question: null as EvaluateQuestion | null, source: 'evaluate' as const })),
          ...developRefs.map(questionRef => ({ questionRef, question: null as EvaluateQuestion | null, source: 'develop' as const })),
          ...extraDevelopRefs.map(questionRef => ({ questionRef, question: null as EvaluateQuestion | null, source: 'develop' as const })),
          ...enquireRefs.map(questionRef => ({ questionRef, question: null as EvaluateQuestion | null, source: 'enquire' as const })),
          ...extraEnquireRefs.map(questionRef => ({ questionRef, question: null as EvaluateQuestion | null, source: 'enquire' as const })),
        ].filter(item => getAreaId(item.questionRef));

        if (linkedItems.length === 0) {
          setQuestionItems([]);
          setAreaTitlesById({});
          setQuestionnaireByAreaId({});
          return;
        }

        const uniqueAreaIds = Array.from(new Set(linkedItems.map(item => getAreaId(item.questionRef))));

        const questionnaireEntries = await Promise.all(
          uniqueAreaIds.map(async (areaId) => {
            try {
              const questionnaire = await GetQuestionnaireByAreaId(areaId);
              return [areaId, questionnaire] as const;
            } catch {
              return [areaId, undefined] as const;
            }
          }),
        );

        const questionnaires: Record<string, IProductQuestionnaire> = Object.fromEntries(
          questionnaireEntries.filter((entry): entry is readonly [string, IProductQuestionnaire] => !!entry[1]),
        );

        setQuestionnaireByAreaId(questionnaires);
        setAreaTitlesById(
          Object.fromEntries(
            uniqueAreaIds.map(areaId => [areaId, questionnaires[areaId]?.title || areaId]),
          ),
        );

        const resolvedQuestions = await Promise.all(
          linkedItems.map(async (item) => {
            const areaId = getAreaId(item.questionRef);
            const questionnaire = questionnaires[areaId];
            const source = questionnaire
              ? sourceForProductType(questionnaire.productType)
              : item.source;

            // Evaluate questions live under `questionGroup`; Develop/Enquire do not, so skip the named Evaluate endpoints.
            if (source !== 'evaluate') {
              return { ...item, source, question: null };
            }

            const questionId = getQuestionId(item.questionRef);
            const questionGroupId = getQuestionGroupId(item.questionRef);
            if (!areaId || !questionId) {
              return { ...item, source, question: null };
            }

            try {
              const question = questionGroupId
                ? await GetEvaluateQuestionByReference(areaId, questionGroupId, questionId)
                : await GetEvaluateQuestionByTrimmedReference(areaId, questionId);
              return { ...item, source, question };
            } catch (fetchError) {
              console.error('Error loading linked evaluate question:', fetchError);
              return { ...item, source, question: null };
            }
          }),
        );

        setQuestionItems(resolvedQuestions);
      } catch (requestError) {
        console.error('Error loading linked diagnostics:', requestError);
        setError('Failed to load linked diagnostic areas. Please try again.');
        setQuestionItems([]);
        setAreaTitlesById({});
        setQuestionnaireByAreaId({});
      } finally {
        setLoading(false);
      }
    };

    loadLinkedDiagnostics();
  }, [objectiveId]);

  const areaIds = useMemo(() => {
    const distinct = new Set(questionItems.map(item => getAreaId(item.questionRef)).filter(Boolean));
    return Array.from(distinct);
  }, [questionItems]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Linked Diagnostic Areas</h2>
        {areaIds.length > 0 && (
          <p className={styles.subtitle}>Linked areas: {areaIds.length}</p>
        )}
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <LoadingIcon />
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <h3>Unable to load linked diagnostics</h3>
            <p>{error}</p>
          </div>
        ) : questionItems.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No linked diagnostic areas</h3>
            <p>This objective is not currently linked to any diagnostic criteria.</p>
          </div>
        ) : (
          <div className={styles.sections}>
            {questionItems.map((item, index) => {
              const areaId = getAreaId(item.questionRef);
              const criteriaId = getQuestionId(item.questionRef);
              const questionnaire = questionnaireByAreaId[areaId];
              const productType = questionnaire?.productType ?? productTypeForSource(item.source);
              const section = criteriaId
                ? questionnaire?.questionnaireSections?.find(sec =>
                    (sec.criteria ?? []).some(criteria => criteria.id === criteriaId),
                  )
                : undefined;
              const criteria = section?.criteria?.find(c => c.id === criteriaId);
              const criteriaTitle = criteria?.title || getQuestionTitleSafe(item.question) || criteriaId;
              const criteriaDescription = criteria?.description || getFullQuestionSafe(item.question);
              const textValues = getAreasForImprovementSafe(item.question)
                || getAreasForImprovement(criteria?.fields ?? [])
                  .map(field => field.content)
                  .filter(Boolean)
                  .join('\n');

              const hasCriteria = Boolean(criteriaId);
              const hasExtraFields = !!(criteriaDescription || textValues);
              const canNavigate = productType !== ProductType.Develop || developEnabled;
              const diagnosticPath = (() => {
                const base = `/${getProductTypePathSegment(productType)}/${areaId}`;
                if (section?.id && criteriaId) {
                  return `${base}/${section.id}/${criteriaId}`;
                }
                return base;
              })();

              return (
                <div
                  key={`${item.source}-${areaId}-${criteriaId || 'area'}-${index}`}
                  className={styles.sectionCard}
                >
                  <div className={styles.cardTop}>
                    <h3 className={styles.sectionTitle}>
                      {areaTitlesById[areaId] ?? areaId}
                    </h3>
                    {canNavigate && (
                      <Button
                        size="Small"
                        colour="#FFF"
                        textColour="#000"
                        border="rgba(0,0,0,0.2)"
                        clickHandler={() => navigate(diagnosticPath)}
                      >
                        Go to diagnostic
                      </Button>
                    )}
                  </div>
                  {hasCriteria && (
                    <div className={styles.breadcrumb}>
                      <span className={styles.breadcrumbSegment}>{section?.title || 'Unknown section'}</span>
                      <span className={styles.breadcrumbSeparator}>›</span>
                      <span className={styles.breadcrumbSegment}>{criteriaTitle}</span>
                    </div>
                  )}
                  {hasExtraFields && (
                    <div className={styles.extraFields}>
                      {criteriaDescription && (
                        <div className={styles.field}>
                          <p className={styles.fieldLabel}>Sub Criteria</p>
                          <p className={styles.fieldValue}>{criteriaDescription}</p>
                        </div>
                      )}
                      {textValues && (
                        <div className={styles.field}>
                          <p className={styles.fieldLabel}>Text Values</p>
                          <p className={styles.fieldValue}>{textValues}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedDiagnosticsPopup;
